import { useEffect, useRef } from 'react'

import type { VisibleRowRange } from '../ManifestContext'

type ManifestSurface = 'inline' | 'fullscreen'

const DEPTH_BUCKET_SIZE = 10

/**
 * [BP-2378] Fires manifest_scroll_depth (bucketed to 10% increments, so it
 * doesn't fire on every scroll tick) and manifest_fully_viewed (once, at
 * 100%) based on the same visibleRange/total already computed for the
 * row-count footer — not new pixel-math. Replaces BP-2376's "virtual page"
 * stopgap in FullManifestPanel.
 */
export function useManifestScrollDepthTracking({
  enabled,
  onFullyViewed,
  onScrollDepth,
  surface,
  total,
  visibleRange,
}: {
  enabled: boolean
  onFullyViewed: (surface: ManifestSurface) => void
  onScrollDepth: (surface: ManifestSurface, depthPercent: number) => void
  surface: ManifestSurface
  total: number
  visibleRange: VisibleRowRange | null
}) {
  const lastFiredBucketRef = useRef(0)
  const hasFiredFullyViewedRef = useRef(false)

  useEffect(() => {
    if (!enabled || total <= 0) return

    const depthPercent = Math.min(
      100,
      Math.round((((visibleRange?.lastRow ?? -1) + 1) / total) * 100)
    )
    const bucket =
      Math.floor(depthPercent / DEPTH_BUCKET_SIZE) * DEPTH_BUCKET_SIZE

    if (bucket > lastFiredBucketRef.current) {
      lastFiredBucketRef.current = bucket
      onScrollDepth(surface, bucket)
    }

    if (depthPercent >= 100 && !hasFiredFullyViewedRef.current) {
      hasFiredFullyViewedRef.current = true
      onFullyViewed(surface)
    }
  }, [enabled, onFullyViewed, onScrollDepth, surface, total, visibleRange])
}
